import { format, formatDistanceToNow } from 'date-fns'

import { Order } from './types'

export type UiOrderStatus = 'pending' | 'in_progress' | 'ready' | 'on_the_way' | 'completed' | 'cancelled'

// Единая логика статуса — её же использует бейдж на карточке и группировка по табам,
// чтобы не разъезжаться в двух местах.
export const deriveOrderStatus = (order: Order): UiOrderStatus => {
  const { preparationStatus, fulfillmentStatus, externalDelivery } = order

  if (preparationStatus === 'cancelled') return 'cancelled'
  if (preparationStatus === 'archived' || fulfillmentStatus === 'fulfilled') return 'completed'

  // 'dropped_off' — единственный финальный стейт доставки в Shop::ExternalDelivery,
  // значения 'delivered' в enum нет
  if (externalDelivery?.state === 'dropped_off') return 'completed'
  if (externalDelivery?.state === 'picked_up' || externalDelivery?.state === 'arrived_at_dropoff') {
    return 'on_the_way'
  }

  if (preparationStatus === 'ready_for_pickup') return 'ready'
  if (preparationStatus === 'in_progress') return 'in_progress'

  return 'pending'
}

export type OrderGroup = {
  active: Order[]
  history: Order[]
  upcoming: Order[]
}

const bucketFor = (order: Order): keyof OrderGroup => {
  const status = deriveOrderStatus(order)

  if (status === 'cancelled' || status === 'completed') return 'history'

  if (order.fulfillment?.scheduleType === 'scheduled' && order.fulfillment.fulfillmentAt) {
    if (new Date(order.fulfillment.fulfillmentAt) > new Date()) return 'upcoming'
  }

  return 'active'
}

const ACTIVE_SORT_WEIGHT: Record<UiOrderStatus, number> = {
  ready: 0,
  on_the_way: 0,
  in_progress: 1,
  pending: 2,
  completed: 3,
  cancelled: 3,
}

export const groupOrders = (orders: Order[]): OrderGroup => {
  const grouped: OrderGroup = { active: [], upcoming: [], history: [] }

  orders.forEach((order) => {
    grouped[bucketFor(order)].push(order)
  })

  grouped.active.sort((a, b) => {
    const diff = ACTIVE_SORT_WEIGHT[deriveOrderStatus(a)] - ACTIVE_SORT_WEIGHT[deriveOrderStatus(b)]
    return diff !== 0 ? diff : new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  })

  grouped.upcoming.sort((a, b) => {
    const aDate = a.fulfillment?.fulfillmentAt ? new Date(a.fulfillment.fulfillmentAt) : new Date(a.createdAt)
    const bDate = b.fulfillment?.fulfillmentAt ? new Date(b.fulfillment.fulfillmentAt) : new Date(b.createdAt)
    return aDate.getTime() - bDate.getTime()
  })

  grouped.history.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())

  return grouped
}

export const getDisplayId = (order: Order): string =>
  order.uuid ? order.uuid.slice(0, 8) : order.id ? order.id : 'Order'

export const getScheduledLabel = (order: Order): string | null => {
  const { fulfillment } = order
  if (!fulfillment) return null

  if (fulfillment.scheduleType === 'scheduled' && fulfillment.fulfillmentAt) {
    return `Scheduled for ${format(new Date(fulfillment.fulfillmentAt), 'MMM d, h:mm a')}`
  }
  if (fulfillment.scheduleType === 'asap') return 'ASAP'
  return null
}

export const getDateLabel = (order: Order, status: UiOrderStatus): string | null => {
  if (status === 'completed' || status === 'cancelled') {
    const verb = status === 'cancelled' ? 'Cancelled' : 'Completed'
    return `${verb} ${formatDistanceToNow(new Date(order.updatedAt), { addSuffix: true })}`
  }
  if (order.createdAt) {
    return `Ordered ${formatDistanceToNow(new Date(order.createdAt), { addSuffix: true })}`
  }
  return null
}

export const getItemsSummary = (order: Order): string => {
  const lineItems = order.lineItems ?? []
  const totalItems = lineItems.reduce((sum, item) => sum + (item.quantity || 0), 0)
  const itemNames = lineItems
    .slice(0, 2)
    .map((item) => item.orderable.name)
    .join(', ')

  if (totalItems === 0) return 'No items'
  if (totalItems <= 2) return itemNames
  return `${itemNames} +${totalItems - 2} more`
}

export const getEstimatedDeliveryLabel = (order: Order): string | null => {
  const { externalDelivery } = order
  if (!externalDelivery) return null

  if (externalDelivery.state === 'dropped_off') return 'Delivered'
  if (externalDelivery.estimatedDeliveryTime) {
    return `~${format(new Date(externalDelivery.estimatedDeliveryTime), 'h:mm a')}`
  }
  return null
}
